import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import {
  Route, Routes, Navigate, useLocation, useOutletContext,
} from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';

import { apiRequest } from './authConfig';
import { ApiClient } from './api';
import { EmptyState, ErrorState, LoadingState } from './muster';
import type { Fachbereich, Projekt } from './api';
import { AppShell } from './rahmen/AppShell';
import { useKopfversion } from './rahmen/kopfversion';
import { useProjektwahl } from './rahmen/projektwahl';
import type { Projektstand } from './rahmen/projektwahl';
import { alleEintraege, eintragsoptionen } from './navigation/struktur';
import { Zustandsseite } from './seiten/Zustandsseite';
import { Auftraege } from './seiten/Auftraege';
import { Auftragsdetail } from './seiten/Auftragsdetail';
import { Projektrahmen } from './seiten/Projektrahmen';
import type { Rahmen } from './seiten/Projektrahmen';
import { Parameterfuellgrad } from './seiten/Parameterfuellgrad';
import { Berichte } from './seiten/Berichte';
import { Projektuebersicht } from './seiten/Projektuebersicht';
import { Planstaende } from './seiten/Planstaende';
import { BimModelleSeite } from './seiten/BimModelle';
import { Einstellungen } from './seiten/Einstellungen';
import { Profile } from './seiten/Profile';
import { HeizungKaelte } from './seiten/HeizungKaelte';
import { HeizungDatenpflege } from './seiten/HeizungDatenpflege';
import { HeizungBerechnung } from './seiten/HeizungBerechnung';
import { HeizungNetzbruecken } from './seiten/HeizungNetzbruecken';
import { Kaelte } from './seiten/Kaelte';
import { KaelteNachweis } from './seiten/KaelteNachweis';
import { HeizungAbgleich } from './seiten/HeizungAbgleich';
// TH-2: Erzeugung und Absicherung (Erzeugerbilanz, Kaskade, Absicherung, Ausdehnung, J15).
import { HeizungErzeugung } from './seiten/HeizungErzeugung';
import { HeizungSollgeraete } from './seiten/HeizungSollgeraete';
import { ElektroBaum } from './seiten/ElektroBaum';
import { ElektroRahmen } from './seiten/elektro/ElektroRahmen';
import { ElektroVerteiler } from './seiten/ElektroVerteiler';
import { ElektroVerteilerplanung } from './seiten/ElektroVerteilerplanung';
import { ElektroItVerteiler } from './seiten/ElektroItVerteiler';
import { ElektroCctv } from './seiten/ElektroCctv';
import { ElektroCctvDetail } from './seiten/ElektroCctvDetail';
import { ElektroDatenpflege } from './seiten/ElektroDatenpflege';
import { Normwerte } from './seiten/Normwerte';
import { ElektroMeldelinien } from './seiten/ElektroMeldelinien';
import { ElektroKabeltrassen } from './seiten/ElektroKabeltrassen';
import { ElektroMelderbedarf } from './seiten/ElektroMelderbedarf';
import { ElektroSicherheitstechnik } from './seiten/ElektroSicherheitstechnik';
import { ElektroBma } from './seiten/ElektroBma';
import { ElektroSprachalarmSeite, ElektroZutrittSeite } from './seiten/elektro/SicherheitstechnikSeiten';
import { ElektroSicherheitsstrom } from './seiten/ElektroSicherheitsstrom';
import { ElektroVerbraucher } from './seiten/ElektroVerbraucher';
import { ElektroEventstrom } from './seiten/ElektroEventstrom';
import { ElektroNea } from './seiten/ElektroNea';
import { ElektroPv } from './seiten/ElektroPv';
import { ElektroBlitzschutz } from './seiten/ElektroBlitzschutz';
import { ElektroFunktionserhalt } from './seiten/ElektroFunktionserhalt';
import { ElektroVersammlungsstaette } from './seiten/ElektroVersammlungsstaette';
import { ElektroKabelliste } from './seiten/ElektroKabelliste';
import { ElektroSimaris } from './seiten/ElektroSimaris';
import { ElektroBeleuchtung } from './seiten/ElektroBeleuchtung';
import { ElektroBeleuchtungPunktverfahren } from './seiten/ElektroBeleuchtungPunktverfahren';
import { ElektroSicherheitsbeleuchtung } from './seiten/ElektroSicherheitsbeleuchtung';
import { ElektroDali } from './seiten/ElektroDali';
import { Lueftung } from './seiten/lueftung/Lueftung';
import { Funktionalbeschreibung } from './seiten/Funktionalbeschreibung';
import { Sanitaer } from './seiten/Sanitaer';
import {
  SanitaerTrinkwasser, SanitaerWarmwasser, SanitaerSchmutzwasser,
  SanitaerFallleitungen, SanitaerHebeanlagen, SanitaerFettabwasser,
  SanitaerRegenwasser, SanitaerTeilstreckenSeite, SanitaerVerortungSeite,
  SanitaerGegenstaendeSeite, SanitaerAnlagenSeite, SanitaerProdukteSeite,
  SanitaerBefunde, SanitaerUeberflutung, SanitaerDruckzonenSeite,
  SanitaerAbsicherungSeite,
} from './seiten/SanitaerSeiten';
import {
  AussenanlagenRahmen, AussenBestandSeite, AussenSanitaerSeite, AussenElektroSeite,
  AussenHydrantenSeite, AussenGeraeteSeite, AussenVersickerungSeite,
  AussenBerechnungSeite, AussenNachweisSeite,
} from './seiten/Aussenanlagen';
import { AussenRueckhaltungSeite } from './seiten/AussenRueckhaltung';
import { Aussparungen } from './seiten/Aussparungen';
import {
  AussparungAbgleichSeite, AussparungArbeitSeite, AussparungBaumSeite,
} from './seiten/AussparungSeiten';
import { Ausschreibung } from './seiten/Ausschreibung';
import {
  AusschreibungGesamtSeite, AusschreibungGrundlagenSeite,
} from './seiten/AusschreibungSeiten';
import { Fremdmodelle } from './seiten/Fremdmodelle';
import { Fremdmodellaufnahme } from './seiten/Fremdmodellaufnahme';
import {
  Kuechenplanung, KuecheAnschluesseSeite, KuechePlaeneSeite, KuecheRaeumeSeite,
} from './seiten/Kuechenplanung';
import { Kuechenaufnahme } from './seiten/Kuechenaufnahme';
import { Kuechenmapping } from './seiten/Kuechenmapping';
import { Kuechenqualitaet } from './seiten/Kuechenqualitaet';
import { Koordination } from './seiten/Koordination';
import {
  KoordinationEinstufungsregelnSeite, KoordinationGewerkevorrangSeite,
  KoordinationGleichrangregelnSeite, KoordinationHoehenbaenderSeite,
  KoordinationLoesungsartenSeite, KoordinationToleranzenSeite,
  KoordinationKiAnbieterSeite, KoordinationPlanungsabschnitteSeite,
  KoordinationPflichtbauteileSeite, KoordinationKollisionenUndEinstufungSeite,
  KoordinationAuswertungSeite, KoordinationPlaneraufgabenSeite,
  KoordinationAufnahmeUndStandSeite,
} from './seiten/KoordinationSeiten';
import { AiCompletion } from './seiten/AiCompletion';
import {
  MsrRahmen, MsrUebersichtSeite, MsrBaumSeite, MsrAspsSeite, MsrDatenpunkteSeite,
  MsrFunktionenSeite, MsrRaumautomationSeite, MsrSchemenSeite, MsrDokumenteSeite,
  MsrAksSeite, MsrGrundlagenSeite, MsrMbusSeite, MsrBefundeSeite, MsrAnnahmenSeite,
} from './seiten/Msr';
import { Belegung } from './seiten/Belegung';
import { Reservierungen } from './seiten/Reservierungen';
import { Nachtlauf } from './seiten/Nachtlauf';
import { Sammelaktionen } from './seiten/Sammelaktionen';
import { Projektleitung } from './seiten/Projektleitung';
import {
  SprinklerRahmen, SprinklerBestandSeite, SprinklerRaeumeSeite, SprinklerNetzeSeite,
  SprinklerKoepfeSeite, SprinklerBerechnungSeite, SprinklerNachweisSeite,
  SprinklerSteigleitungSeite, SprinklerGeraeteSeite, SprinklerTechnikSeite,
  SprinklerKonfigurationSeite,
} from './seiten/Sprinkler';
import { Lueftungsvorlagen } from './seiten/lueftung/Lueftungsvorlagen';
import { Datenblaetter } from './seiten/lueftung/Datenblaetter';
import { Datenblattimporte } from './seiten/lueftung/Datenblattimporte';
// TV5 Lüftung „Prüfungen sichtbar“: vier Prüfungsseiten (Gliederung seiten/lueftung/abschnitte.ts).
import { LueftungGeraetepruefung } from './seiten/lueftung/LueftungGeraetepruefung';
import { LueftungBrandschutz } from './seiten/lueftung/LueftungBrandschutz';
import { LueftungLuftmengen } from './seiten/lueftung/LueftungLuftmengen';
import { LueftungPruefbefunde } from './seiten/lueftung/LueftungPruefbefunde';
import { Bauteilbefunde } from './seiten/lueftung/Bauteilbefunde';
import { Anlagenliste } from './seiten/lueftung/Anlagenliste';
import { Druckverlust } from './seiten/lueftung/Druckverlust';
import { Schall } from './seiten/lueftung/Schall';
import { Raumschall } from './seiten/lueftung/Raumschall';
import { Kreislaufverbund } from './seiten/lueftung/Kreislaufverbund';
import { LueftungBrandschutzklappen } from './seiten/lueftung/LueftungBrandschutzklappen';
import { Maschinen } from './seiten/Maschinen';
import { Installation } from './seiten/Installation';
import { Raumbuch } from './seiten/Raumbuch';
import { Modellvorgaben } from './seiten/Modellvorgaben';
import { Tuerbuch } from './seiten/Tuerbuch';
import { Tuertypen } from './seiten/Tuertypen';
import {
  Gebaeudehuelle, HeizlastKuehllastRahmen, HeizlastRechnen,
  HeizlastKlimaSeite, HeizlastAllgemeinSeite,
} from './seiten/Gebaeudehuelle';
import { RaumtemperaturenSeite } from './seiten/Raumtemperaturen';
import { AnnahmenVorgabenSeite } from './seiten/heizlastkuehllast/AnnahmenVorgaben';
import { GegAbgleichSeite } from './seiten/heizlastkuehllast/GegAbgleich';
import { KuehllastSeite, KuehllastJahresgangSeite } from './seiten/Kuehllast';
import { Huellenbaum } from './seiten/Huellenbaum';
import { ElektroGeraete } from './seiten/ElektroGeraete';
import {
  Geraeteliste, ProduktseiteIntern, ProduktseiteOeffentlich,
  KomponenteOeffentlich, KomponenteIntern,
  SanitaerGeraetIntern, ElektroGeraetIntern,
  SanitaerseiteOeffentlich, ElektroseiteOeffentlich,
  SprinklerGeraetIntern, AussenGeraetIntern,
  SprinklerseiteOeffentlich, AussenseiteOeffentlich,
} from './seiten/Produktseite';
import {
  Anlagenseiten, AnlagenseiteIntern, AnlagenseiteOeffentlich,
} from './seiten/lueftung/Anlagenseite';
// AC1a: ACC verbinden (Verbindung zu Autodesk, Zustimmung, Widerruf).
import { AccVerbindenSeite } from './seiten/acc/AccVerbinden';

export function App() {
  const { instance } = useMsal();
  const angemeldet = useIsAuthenticated();

  // Der Client haengt an der MSAL-Instanz und muss nicht bei jedem Rendern
  // neu entstehen.
  const api = useMemo(() => new ApiClient(instance), [instance]);

  // Der Pfad wird VOR der Anmeldepruefung gebraucht - siehe unten.
  const pfad = useLocation().pathname;

  // ═════════════════════════════════════════════════════════════════════════
  // EIN PROJEKT, UND ES STEHT IN DER KOPFZEILE
  // ═════════════════════════════════════════════════════════════════════════
  //
  // Festlegung des Fachplaners vom 20.08.2026: Parallele Projekte laufen
  // kuenftig in GETRENNTEN Containern, nicht nebeneinander in einer
  // Datenbank. Der Grund ist nicht die Anzeige, sondern der Umbau: Ein
  // Projekt mit anderer Aufgabe braucht andere Datenstrukturen,
  // Berechnungslogiken und Anforderungen — und wer die umbaut, baut die
  // Grundlage des anderen Projekts mit um. Das zerstoert vorhandene Daten.
  //
  // Deshalb gab es hier bis v0.53.5 keine Projektliste, sondern das eine
  // Projekt dieses Containers (`liste[0]`).
  //
  // MP-6 (F324, Mehrprojektplattform): Neue Projekte bekommen eine eigene
  // Datenbank, damit sie sich nicht ueberschneiden - der Grund von oben ist
  // damit auf DB-Ebene geloest, und ein Container fuehrt wieder mehrere
  // Projekte. Aktiv ist das `:id` der Route, sonst das zuletzt gewaehlte, sonst
  // das erste (`rahmen/projektwahl.ts`). Kopfzeile, Seitenleiste und
  // Auftragsliste folgen diesem einen Ergebnis.
  // PT-0a (Projekttrennung): Die Liste enthaelt nur die Projekte, die der
  // Angemeldete sehen darf. Eine LEERE Liste ist deshalb eine Antwort und kein
  // Ladezustand - sonst stuende auf /projekte endlos „geladen“.
  const wahl = useProjektwahl(api, angemeldet);
  const { projekt, stand: projektStand } = wahl;

  // ═════════════════════════════════════════════════════════════════════════
  // DER FACHBEREICHSKATALOG GEHOERT JETZT NACH OBEN
  // ═════════════════════════════════════════════════════════════════════════
  //
  // Die Seitenleiste zeigt die Gewerke dieses Projekts und steht auf JEDER
  // Seite - auch auf /auftraege und /maschinen, die ausserhalb des
  // Projektrahmens liegen. Wuerde der Katalog wie bisher nur im
  // `Projektrahmen` geladen, verlore die Seitenleiste beim Sprung auf die
  // Auftragsliste die halbe Navigation.
  //
  // Geladen wird er deshalb hier, einmal, und an den `Projektrahmen`
  // weitergereicht (`bereicheVorab`) - der laedt ihn dann NICHT ein zweites
  // Mal. Die Zahl der Abfragen bleibt damit gleich wie vorher (Auftrag
  // Abschnitt 30: "Bestehende Performance mindestens halten").
  const [bereiche, setBereiche] = useState<Fachbereich[]>([]);

  // F312: die live laufende Version fuer die Kopfzeile (`v0.53.3`), einmal nach der Anmeldung aus
  // `GET /api/system/version`. Ohne Antwort bleibt sie null und die Kopfzeile zeigt nichts.
  const kopfVersion = useKopfversion(api, angemeldet);

  // MP-6: beim Projektwechsel der Katalog des NEUEN Projekts; bis er da ist, keine Gewerke des alten.
  const projektId = projekt?.id;
  useEffect(() => {
    if (!angemeldet || !projektId) return;

    let aktiv = true;
    setBereiche([]);
    api.projektFachbereiche(projektId)
      .then((b) => { if (aktiv) setBereiche(b); })
      .catch(() => { if (aktiv) setBereiche([]); });
    return () => { aktiv = false; };
  }, [api, angemeldet, projektId]);

  const projektpfad = projekt ? `/projekte/${projekt.id}/uebersicht` : '/projekte';

  // Der oeffentliche Weg, VOR der Anmeldeweiche.
  //
  // Die Zeile darunter faengt sonst alles ab, und ein Monteur mit dem QR-Code
  // aus dem Plan landete auf der Microsoft-Anmeldung. Eigener Rahmen ohne
  // Kopfzeile und Navigation: Jeder Verweis darin fuehrte ins Leere.
  if (pfad.startsWith('/b/')) {
    return (
      <Routes>
        <Route path="/b/:bauteilId" element={<ProduktseiteOeffentlich />} />

        {/* Die Lüftungskomponente. Eigene Route, weil hinter den beiden
            verschiedene Tabellen stehen - eine gemeinsame müsste raten. */}
        <Route path="/lk/:bauteilId" element={<KomponenteOeffentlich />} />
      </Routes>
    );
  }

  // Und dasselbe für die Sanitärbauteile.
  //
  // Dieser Zweig hat gefehlt, obwohl der Endpunkt stand und die Geräteliste
  // den Verweis schon anbot: Er fiel durch die Anmeldeweiche und landete beim
  // Sammelfang auf der Startseite. Ein Monteur mit dem QR-Code hätte die
  // Microsoft-Anmeldung gesehen — genau der Fall, vor dem der Kommentar über
  // /b/ warnt.
  //
  // Ein Verweis, den niemand einmal geöffnet hat, ist kein fertiger Verweis.
  if (pfad.startsWith('/s/')) {
    return (
      <Routes>
        <Route path="/s/:bauteilId" element={<SanitaerseiteOeffentlich />} />
      </Routes>
    );
  }

  // Und der Elektroweg. Drei Pfade fuer drei Tabellen - ein gemeinsamer
  // muesste raten, in welcher die Kennung liegt, und bei einer unbekannten
  // waere nicht zu unterscheiden, ob sie falsch ist oder nur im anderen
  // Gewerk steht.
  // Der Aussenanlagenweg. Eigener Pfad wie bei den uebrigen Gewerken: Ein
  // gemeinsamer muesste raten, in welcher Tabelle die Kennung liegt.
  if (pfad.startsWith('/ab/')) {
    return (
      <Routes>
        <Route path="/ab/:bauteilId" element={<AussenseiteOeffentlich />} />
      </Routes>
    );
  }

  // Und der Sprinklerweg. Eigener Pfad wie bei den uebrigen Gewerken.
  //
  // Er steht VOR der Anmeldeweiche, weil er sonst dort haengenbliebe: Ein
  // Monteur mit dem QR-Code vom Typenschild saehe die Microsoft-Anmeldung -
  // genau der Fall, vor dem der Kommentar oben bei /b/ warnt.
  if (pfad.startsWith('/sp/')) {
    return (
      <Routes>
        <Route path="/sp/:bauteilId" element={<SprinklerseiteOeffentlich />} />
      </Routes>
    );
  }

  if (pfad.startsWith('/e/')) {
    return (
      <Routes>
        <Route path="/e/:bauteilId" element={<ElektroseiteOeffentlich />} />
      </Routes>
    );
  }

  // Derselbe Weg für die Lüftungsanlagen. Ein eigener Pfad und nicht /b/:
  // Eine Anlage ist kein Bauteil — sie besteht aus mehreren, und ihre Kennung
  // kommt aus einer anderen Tabelle.
  if (pfad.startsWith('/a/')) {
    return (
      <Routes>
        <Route path="/a/:plantId" element={<AnlagenseiteOeffentlich />} />
      </Routes>
    );
  }

  if (!angemeldet) return <Anmeldung />;

  const konto = instance.getActiveAccount();

  // ═════════════════════════════════════════════════════════════════════════
  // DER RAHMEN (Block U, Auftrag vom 17.09.2026)
  // ═════════════════════════════════════════════════════════════════════════
  //
  // Hier stand bis zum 17.09.2026 eine `<header className="kopf">` mit
  // Marke, Projekt und Abmeldeknopf und darunter eine
  // `<nav className="navigation">` mit sechs waagerechten Punkten - und im
  // `Projektrahmen` noch einmal zwei weitere waagerechte Zeilen. Drei
  // Menuezeilen uebereinander, und jede neue Funktion machte eine davon
  // laenger.
  //
  // An ihre Stelle tritt der `AppShell`: dunkle Kopfzeile, linke
  // Hauptnavigation, Brotkrumen, Befehlspalette. Die ROUTEN darunter sind
  // Zeile fuer Zeile dieselben geblieben - kein Pfad umbenannt, keine
  // Weiterleitung eingezogen, kein Ziel entfernt. Lesezeichen und Verweise
  // aus dem Add-in gelten unveraendert weiter (belegt durch
  // `pruefung/routen-abgleich.mjs`).
  //
  // Der `AppShell` ist eine Layoutroute OHNE eigenen Pfad. Das ist der
  // Grund, warum die Pfade unveraendert bleiben konnten: Er schiebt sich
  // vor die bestehenden Routen, ohne eine Ebene in die Adresse zu haengen -
  // dasselbe Muster wie `ElektroRahmen` eine Ebene tiefer.
  // BIM12: „BIM-Modelle“ nur, wenn der Server den BIM-Kern freigeschaltet hat (Feld der Projektliste, kein
  // zusaetzlicher Abruf).
  const eintraege = alleEintraege(bereiche, projekt?.id, eintragsoptionen(projekt));

  const projektAnzeige = wahl.eintrag?.bezeichnung ?? null;

  return (
    <Routes>
      <Route
        element={(
          <AppShell
            projektName={projektAnzeige}
            kopf={{
              // Nur, was der Server wirklich liefert: Bei einem Projekt zeigt
              // der Umschalter den Kontext an und bietet keinen Wechsel an, den
              // es nicht gibt (Auftrag Abschnitt 4); bei mehreren (MP-6) die
              // volle Liste, jeweils mit „eigene Projekt-DB“ bzw. „Dienst-DB“.
              projekt: wahl.eintrag,
              projekte: wahl.eintraege,
              onProjektWechsel: wahl.wechseln,
              benutzerName: konto?.username ?? null,
              benutzerAnzeige: konto?.name ?? konto?.username ?? null,
              onAbmelden: () => instance.logoutRedirect(),
              einstellungenPfad: projekt
                ? `/projekte/${projekt.id}/einstellungen`
                : undefined,
              version: kopfVersion,
            }}
            seitenleiste={{ eintraege, projektId: projekt?.id }}
          />
        )}
      >
          <Route path="/" element={<Zustandsseite api={api} />} />
          {/* MP-6: die Auftraege des aktiven Projekts (Abfrage mit projectId, also aus seiner Datenbank);
              solange die Projektliste laedt undefined, ohne Projekt null. */}
          <Route path="/auftraege" element={(
            <Auftraege api={api} projektId={projekt?.id ?? (projektStand === 'laedt' ? undefined : null)} />
          )} />
          <Route path="/auftraege/:id" element={<Auftragsdetail api={api} />} />
          {/* Die Projektliste ist entfallen — ein Container fuehrt genau ein
              Projekt. Der Pfad bleibt trotzdem bestehen und leitet weiter:
              Lesezeichen und Verweise aus dem Add-in laufen sonst ins Leere.
              Solange das Projekt noch geladen wird, steht hier ein Hinweis und
              kein Sprung — ein Sprung auf '/projekte' waere eine Schleife. */}
          <Route path="/projekte" element={(
            <Projekteinstieg projekt={projekt} stand={projektStand} pfad={projektpfad} />
          )} />

          {/* Die Projektansicht ist ein Rahmen mit Reitern. Der Rahmen laedt
              Projekt und Fachbereichskatalog einmal; die Reiter bekommen beides
              ueber den Outlet-Kontext. Die Pfade raumbuch und tuerbuch bleiben
              unveraendert - Lesezeichen und Verweise aus dem Add-in laufen
              weiter. */}
          <Route path="/projekte/:id"
                 element={<Projektrahmen api={api} bereicheVorab={bereiche} />}>
            <Route index element={<Navigate to="uebersicht" replace />} />
            <Route path="uebersicht" element={<Projektuebersicht />} />
            <Route path="planstaende" element={<Planstaende />} />
            {/* BIM12 (F147/F148): Modellregister des BIM-Kerns, nur lesend. Ohne Schalter Bim:Schatten fehlt der
                Navigationseintrag und der Server antwortet 404. */}
            <Route path="bim-modelle" element={<BimModelleSeite />} />
            {/* TX2 (Befund P9): Berichtsregister mit Abruf der abgelegten Fassungen. */}
            <Route path="berichte" element={<Berichteseite />} />
            {/* Der Parameterfuellgrad. Er steckte bisher als Karte in den
                Einstellungen und war ueber kein Menue zu erreichen - eine
                Auswertung ueber alle sieben Gewerkekataloge, versteckt
                zwischen Modellzuordnung und Lagekorrektur. Jetzt eine
                eigene Route; in den Einstellungen steht ein Verweis. Die
                Seite selbst ist unveraendert. */}
            <Route path="parameter-fuellgrad" element={<Parameterfuellgradseite />} />
            <Route path="raumbuch" element={<Raumbuch api={api} />} />
            <Route path="tuerbuch" element={<Tuerbuch api={api} />} />
            <Route path="lueftung" element={<Lueftung />} />
            {/* Gehoert zur Lueftung, hat aber keinen eigenen Reiter - wie
                Tuertypen beim Tuerbuch. */}
            <Route path="lueftung/vorlagen" element={<Lueftungsvorlagen />} />
            <Route path="lueftung/datenblaetter" element={<Datenblaetter />} />
            <Route path="lueftung/importe" element={<Datenblattimporte />} />
            <Route path="lueftung/befunde" element={<Bauteilbefunde />} />
            <Route path="lueftung/anlagen" element={<Anlagenliste />} />
            <Route path="lueftung/druckverlust" element={<Druckverlust />} />
            <Route path="lueftung/schall" element={<Schall />} />

            {/* Der Pegel je RAUM (Befund L-02) - eigene Seite neben der
                Schallkette je Strang: Ein Raum hat selten einen Auslass,
                und die Summe seiner Auslässe ist eine andere Frage als der
                Weg eines einzelnen. */}
            <Route path="lueftung/raumschall" element={<Raumschall />} />
            <Route path="lueftung/kvs" element={<Kreislaufverbund />} />
            {/* TV8 (20.10.2026): Bestand der Brandschutzklappen mit Baum und PDF LUE-BKL. */}
            <Route path="lueftung/brandschutzklappen" element={<LueftungBrandschutzklappen />} />
            {/* TV5 Lüftung „Prüfungen sichtbar“ (Anfang): J9-Geräteprüfung, BSK und
                Entrauchung, Luftmengen je Raum, Befunde des Druckverlustlaufs. */}
            <Route path="lueftung/geraetepruefung" element={<LueftungGeraetepruefung />} />
            <Route path="lueftung/brandschutz" element={<LueftungBrandschutz />} />
            <Route path="lueftung/luftmengen" element={<LueftungLuftmengen />} />
            <Route path="lueftung/pruefbefunde" element={<LueftungPruefbefunde />} />
            {/* TV5 (Ende) */}
            <Route path="sanitaer" element={<Sanitaer />} />
            {/* Die ehemaligen Reiter von Sanitaer.tsx als echte Unterrouten
                (S1, auftraege/S1-sanitaer-navigation-und-baum.md). Die drei
                Abwasserrechnungen bleiben getrennt - sie schneiden nach
                EMPFAENGER, nicht nach Anzeige. */}
            <Route path="sanitaer/anlagen" element={<SanitaerAnlagenSeite />} />
            <Route path="sanitaer/trinkwasser" element={<SanitaerTrinkwasser />} />
            <Route path="sanitaer/warmwasser" element={<SanitaerWarmwasser />} />
            <Route path="sanitaer/schmutzwasser" element={<SanitaerSchmutzwasser />} />
            <Route path="sanitaer/fallleitungen" element={<SanitaerFallleitungen />} />
            <Route path="sanitaer/hebeanlagen" element={<SanitaerHebeanlagen />} />
            <Route path="sanitaer/fettabwasser" element={<SanitaerFettabwasser />} />
            <Route path="sanitaer/regenwasser" element={<SanitaerRegenwasser />} />
            {/* TS9b2: die neuen Sanitärseiten (Gliederung in seiten/sanitaer/abschnitte.ts). */}
            <Route path="sanitaer/ueberflutung" element={<SanitaerUeberflutung />} />
            <Route path="sanitaer/druckzonen" element={<SanitaerDruckzonenSeite />} />
            <Route path="sanitaer/absicherung" element={<SanitaerAbsicherungSeite />} />
            <Route path="sanitaer/teilstrecken" element={<SanitaerTeilstreckenSeite />} />
            <Route path="sanitaer/verortung" element={<SanitaerVerortungSeite />} />
            <Route path="sanitaer/gegenstaende" element={<SanitaerGegenstaendeSeite />} />
            <Route path="sanitaer/produkte" element={<SanitaerProdukteSeite />} />
            <Route path="sanitaer/befunde" element={<SanitaerBefunde />} />

            {/* Die Funktionale Leistungsbeschreibung (Plan Abschnitt 7). Kein
                Gewerk, sondern eine Sicht QUER ueber alle: gegliedert nach den
                Kostengruppen der DIN 276. Deshalb steht sie neben den Gewerken
                und nicht in einem von ihnen. */}
            <Route path="funktionalbeschreibung" element={<Funktionalbeschreibung />} />
            <Route path="huelle" element={<Gebaeudehuelle />} />
            {/* Die Bauteilarten mit ihren Kennwerten. Kein eigener Reiter - sie
                wird aus der Huellenseite aufgerufen, wie die Rohrnetzberechnung
                aus der Heizungsseite: Wer sie braucht, kommt vom Datenstand her. */}
            <Route path="huelle/bauteilarten" element={<Huellenbaum />} />
            {/* Heiz-/Kühllast (Muster N1, Plan Abschnitt 3.1): fünf
                interne Reiter wurden fünf echte Unterrouten - Rahmen mit
                `LocalTabs` links, `HeizlastKuehllastRahmen`, wie bei
                Elektro/Fremdmodelle. Der Pfad `heizlast-kuehllast` selbst
                bleibt unveraendert und zeigt den Index-Reiter "Heizlast".
                Die Gliederung steht in seiten/heizlastkuehllast/abschnitte.ts
                und wird dort NUR gelesen. */}
            <Route path="heizlast-kuehllast" element={<HeizlastKuehllastRahmen />}>
              <Route index element={<HeizlastRechnen />} />
              <Route path="kuehllast" element={<KuehllastSeite />} />
              {/* Der Jahresgang der KÜHLLAST - der Jahresgang der Heizlast
                  bleibt in der Route "Heizlast" eingebettet, siehe
                  Kommentar bei `KuehllastJahresgangSeite`. */}
              <Route path="jahresgang" element={<KuehllastJahresgangSeite />} />
              <Route path="klima" element={<HeizlastKlimaSeite />} />
              {/* Die Raumtemperaturanforderungen (Paket RT1): die Vorlage
                  des Fachplaners vom 26.06.2026, die Zuordnungsregeln und
                  je Raum die Herkunft der Solltemperatur. Eigener Reiter und
                  keine Karte in "Standort und Klima": Dort stehen die
                  AUSSEN-Randbedingungen, hier die INNEN-Sollwerte, und die
                  Liste ist raumweise und damit lang. */}
              <Route path="raumtemperaturen" element={<RaumtemperaturenSeite />} />
              <Route path="allgemein" element={<HeizlastAllgemeinSeite />} />
              {/* TL2: Annahmen und Vorgaben - gelesene Vorgaben der Auslegungsläufe,
                  bestätigen mit Begründung und Quelle (TX-STANDARD S1.6/S3.4). */}
              <Route path="annahmen" element={<AnnahmenVorgabenSeite />} />
              {/* TL10: GEG-Abgleich Modell <-> Nachweis je Bauteiltyp - nur Uebernahme und Abgleich (E5). */}
              <Route path="geg-abgleich" element={<GegAbgleichSeite />} />
            </Route>
            <Route path="heizung-kaelte" element={<HeizungKaelte />} />
            {/* Die Rohrnetzberechnung - Stufe 5. Kein eigener Reiter, sie wird
                aus der Heizungsseite aufgerufen: Wer sie braucht, kommt vom
                Netz her und nicht aus der Reiterleiste. */}
            <Route path="heizung-berechnung" element={<HeizungBerechnung />} />
            {/* Die Netzbruecken - was das Netz zusammenhaelt. Eigene Route
                und eigener Baumknoten: Sie sind keine Zahl der Rechnung,
                sondern ihre VORAUSSETZUNG, und wer sie sucht, sucht sie
                nicht unter einer Foerderhoehe. */}
            <Route path="heizung-netzbruecken" element={<HeizungNetzbruecken />} />
            {/* DIE KAELTESEITE (HZ5). Der Knoten heisst "Heizung/Kaelte" und
                hatte fuenf Kinder, von denen KEINES Kaelte war. Erzeugung,
                Rueckkuehlung, Eisspeicher und freie Kuehlung mit ihren
                Kennwerten - und jeder Kennwert mit der Randbedingung, unter
                der er gilt. */}
            <Route path="kaelte" element={<Kaelte />} />
            {/* Der Kaeltenachweis (TH-3): Kaelte-Kern Stufe 1 und DIN EN 378 -
                juengster Lauf von kaelte-rechnen, Bericht KAE-KMN. */}
            <Route path="kaelte-nachweis" element={<KaelteNachweis />} />
            {/* Der hydraulische Abgleich (TH-4): Einstellwerte je Verbraucher, Strangarmaturen,
                Pumpen - jüngster Lauf heizung-abgleich. */}
            <Route path="heizung-abgleich" element={<HeizungAbgleich />} />
            {/* TH-2: Erzeugung und Absicherung - liest heizung-erzeuger, heizung-ausdehnung, J15. */}
            <Route path="heizung-erzeugung" element={<HeizungErzeugung />} />
            {/* Die Datenpflege quer zur Baumansicht: welche Typen haben noch kein
                Datenblatt. Kein eigener Reiter - sie wird aus der Heizungsseite
                aufgerufen. */}
            <Route path="heizung-datenpflege" element={<HeizungDatenpflege />} />
            {/* Die SOLLSEITE des Gewerks: die Geraeteliste des Fachplaners.
                Sie steht NEBEN "geraete" (dem Bestand aus dem Modell) und
                nicht darin - eine zugesagte Zahl neben einer gemessenen,
                ohne dass man den Unterschied noch sieht, waere genau der
                Fehler, den dieses Paket sichtbar machen soll. */}
            <Route path="heizung-sollgeraete" element={<HeizungSollgeraete />} />
            {/* Die Geraeteliste und die Produktseite. Im Rahmen, damit die
                Reiterleiste stehenbleibt - wie bei tuertypen. Der Pfad heisst
                geraete und nicht maschinen: /maschinen ist vergeben, dort
                stehen die Revit-Rechner. */}
            {/* Die Komponentenseite der Lüftung. Im Rahmen wie die
                Geräteseite, damit die Reiterleiste stehenbleibt. */}
            <Route path="lueftung/komponente/:komponenteId"
                   element={<KomponenteIntern api={api} />} />
            <Route path="geraete" element={<Geraeteliste api={api} />} />
            <Route path="sanitaer/geraete/:bauteilId"
                   element={<SanitaerGeraetIntern api={api} />} />
            <Route path="geraete/:bauteilId"
                   element={<ProduktseiteIntern api={api} />} />
            <Route path="anlagen" element={<Anlagenseiten api={api} />} />
            <Route path="anlagen/:plantId" element={<AnlagenseiteIntern api={api} />} />
            {/* Alle Elektroseiten unter einem Rahmen: links die Gliederung
                nach Abschnitten, rechts die gewaehlte Seite (Muster wie
                Heizung/Kaelte). Die Layoutroute traegt KEINEN eigenen Pfad -
                jede Route darunter behaelt ihren Pfad, Deep Links gelten
                unveraendert weiter. Die Gliederung steht in
                seiten/elektro/abschnitte.ts und NUR dort. */}
            <Route element={<ElektroRahmen />}>
            <Route path="elektro" element={<ElektroBaum />} />
            <Route path="elektro-geraete"
                   element={<ElektroGeraete />} />
            <Route path="elektro-geraete/:bauteilId"
                   element={<ElektroGeraetIntern api={api} />} />
            <Route path="elektro-verteiler" element={<ElektroVerteiler />} />
            <Route path="elektro-datenpflege" element={<ElektroDatenpflege />} />
            <Route path="elektro-meldelinien" element={<ElektroMeldelinien />} />
            <Route path="elektro-melderbedarf"
                   element={<ElektroMelderbedarf />} />
            {/* W2-D1: Brandfallsteuermatrix, Notstrom BMZ/RWA, Ring- und RWA-Nachweis */}
            <Route path="elektro-sicherheitstechnik"
                   element={<ElektroSicherheitstechnik />} />
            {/* BM-1: Abschnitt Brandmeldeanlage (Uebersicht, Heft ELT-BMA mit Kapitelwahl,
                BMA-Listen); Sprachalarm und Zutritt mit eigener Route (E2). */}
            <Route path="elektro-bma" element={<ElektroBma />} />
            <Route path="elektro-sprachalarm" element={<ElektroSprachalarmSeite />} />
            <Route path="elektro-zutritt" element={<ElektroZutrittSeite />} />
            <Route path="elektro-sicherheitsstrom"
                   element={<ElektroSicherheitsstrom />} />
            {/* W2-B2: Verbraucherschnittstellen - nur lesend, die Bilanz bleibt unberuehrt */}
            <Route path="elektro-verbraucher" element={<ElektroVerbraucher />} />
            {/* W2-B1: Eventstromversorgung - nur lesend, die Bilanz bleibt unberuehrt */}
            <Route path="elektro-eventstrom" element={<ElektroEventstrom />} />
            {/* J16: NEA (EG4) und SIMARIS (EG3) - nur lesend, ohne PDF-Knopf
                (die PDF-Routen vergeben bei jedem Aufruf eine Registernummer). */}
            <Route path="elektro-nea" element={<ElektroNea />} />
            {/* W2-A3: PV-Anlage - Netzanbindung, Spannen aus der FLB, Pruefliste */}
            <Route path="elektro-pv" element={<ElektroPv />} />
            {/* W2-C1/C2: Blitzschutz, Erdung, Ueberspannung und Potentialausgleich */}
            <Route path="elektro-blitzschutz" element={<ElektroBlitzschutz />} />
            {/* W2-C3: Systemnachweis Funktionserhalt */}
            <Route path="elektro-funktionserhalt" element={<ElektroFunktionserhalt />} />
            {/* EK-Folgepaket: Regelpruefung Versammlungsstaette (DIN VDE 0100-718) */}
            <Route path="elektro-versammlungsstaette" element={<ElektroVersammlungsstaette />} />
            <Route path="elektro-kabelliste" element={<ElektroKabelliste />} />
            <Route path="elektro-simaris" element={<ElektroSimaris />} />
            {/* Der Beleuchtungsnachweis DIN EN 12464-1 (Paket V-E4).
                Eigene Unterseite und kein Abschnitt des Elektrobaums: Der
                Baum zeigt, WAS wo haengt; der Nachweis zeigt, ob es REICHT -
                und dafuer braucht er Raumbuch, Raumbegrenzungen und den
                Normkatalog, von denen der Baum nichts weiss. */}
            <Route path="elektro-beleuchtung"
                   element={<ElektroBeleuchtung />} />
            {/* Punktverfahren (Paket H4): Ergaenzung des Beleuchtungs-
                nachweises um Ee, Emin, U0 je Raum mit SVG-Grundriss. Baut
                auf V-E4 auf (siehe Seitenkommentar dort). */}
            <Route path="elektro-beleuchtung-punktverfahren"
                   element={<ElektroBeleuchtungPunktverfahren />} />
            {/* Die Sicherheitsbeleuchtung nach DIN EN 1838. Eigene
                Unterseite aus demselben Grund wie die Beleuchtung: eine
                andere Frage (reicht der Rettungsweg?) an einer anderen
                Datenbasis (Türbuch-Raumfolge statt Raumbuch-Tätigkeit). */}
            <Route path="elektro-sicherheitsbeleuchtung"
                   element={<ElektroSicherheitsbeleuchtung />} />
            {/* DALI-Adressen und Lichtszenen (Paket H2, Migration 0575). */}
            <Route path="elektro-dali" element={<ElektroDali />} />
            {/* Die Kabeltrassen. Eigene Unterseite, weil sie nach Stromart
                gegliedert sind und nicht nach Bauteilart: Die Frage ist
                nicht 'welches Fabrikat', sondern 'was liegt zusammen drin'. */}
            <Route path="elektro-kabeltrassen"
                   element={<ElektroKabeltrassen />} />
            {/* Die Verteilerplanung (Paket VT3): Bestückung, Stromkreise
                und Kennzahlen je Verteiler, wie sie die Verteiler-
                bestückung (Paket VT1) aus dem Bestand gebildet hat, mit
                Abrufknopf für den Bericht ELT-VTP. Route steht eigenstaendig;
                die Verlinkung im linken Baum (abschnitte.ts) haengt die
                Steuerung beim Zusammenfuehren ein. */}
            <Route path="elektro-verteilerplanung"
                   element={<ElektroVerteilerplanung />} />
            {/* Der IT-Verteiler (Auftrag Fachplaner 18.09.2026): aus der
                SV/UV-Ansicht herausgelöst, eigener Abschnitt in
                abschnitte.ts. Muster wie elektro-verteiler: Baum links,
                Schrankinhalt rechts. */}
            <Route path="elektro-it-verteiler"
                   element={<ElektroItVerteiler />} />
            <Route path="elektro-cctv"
                   element={<ElektroCctv />} />
            <Route path="elektro-cctv/:kameraId"
                   element={<ElektroCctvDetail />} />
            </Route>
            {/* MSR steht hier NICHT mehr als vorbereiteter Bereich (Batch B4).
                Derselbe Grund wie bei Sprinkler weiter unten: Der Platzhalter
                blieb stehen, als die Seite dazukam, und weil er in der Liste
                vorne liegt, gewann er - die MSR-Seite waere gebaut, ausgerollt
                und unerreichbar. */}
            {/* MSR (Muster N1, Paket N2, Plan Abschnitt 3.3): elf interne
                Reiter (`?reiter=`) wurden elf echte Unterrouten - Rahmen mit
                `LocalTabs` links, `MsrRahmen`, wie bei Heiz-/Kuehllast. Der
                Pfad `msr` selbst bleibt unveraendert und zeigt den
                Index-Reiter "Uebersicht". Die Gliederung steht in
                seiten/msr/abschnitte.ts und wird dort NUR gelesen. */}
            <Route path="msr" element={<MsrRahmen />}>
              <Route index element={<MsrUebersichtSeite />} />
              {/* TM-8a: die projektweite Befundliste (TX-STANDARD S1.5). */}
              <Route path="befunde" element={<MsrBefundeSeite />} />
              <Route path="baum" element={<MsrBaumSeite />} />
              <Route path="asps" element={<MsrAspsSeite />} />
              <Route path="datenpunkte" element={<MsrDatenpunkteSeite />} />
              <Route path="funktionen" element={<MsrFunktionenSeite />} />
              <Route path="raumautomation" element={<MsrRaumautomationSeite />} />
              <Route path="mbus" element={<MsrMbusSeite />} />
              <Route path="schemen" element={<MsrSchemenSeite />} />
              <Route path="aks" element={<MsrAksSeite />} />
              <Route path="dokumente" element={<MsrDokumenteSeite />} />
              <Route path="grundlagen" element={<MsrGrundlagenSeite />} />
              {/* TM-8b: Annahmen und Vorgaben (TX-STANDARD S1.6), Gliederung unter „Planung“ nach Grundlagen. */}
              <Route path="annahmen" element={<MsrAnnahmenSeite />} />
            </Route>
            {/* Die Aussenanlagen stehen VOR den Aussparungen: Sie sind
                ein Gewerk, die Aussparungen ein Querschnittsthema ueber
                alle Gewerke. */}
            {/* Aussenanlagen (Muster N1, Paket N2, Plan Abschnitt 3.4): acht
                interne Reiter (`useState('bestand')`) wurden acht echte
                Unterrouten - Rahmen mit `LocalTabs`, `AussenanlagenRahmen`,
                wie bei Heiz-/Kuehllast und MSR. Der Pfad `aussenanlagen`
                selbst bleibt unveraendert und zeigt den Index-Reiter
                "Bestand". `AussenBerechnung` und `AussenNachweis` hatten
                bis hierher KEINE eigene Route - jetzt haben sie eine. Die
                Gliederung steht in seiten/aussenanlagen/abschnitte.ts und
                wird dort NUR gelesen. */}
            <Route path="aussenanlagen" element={<AussenanlagenRahmen />}>
              <Route index element={<AussenBestandSeite />} />
              <Route path="sanitaer" element={<AussenSanitaerSeite />} />
              <Route path="elektro" element={<AussenElektroSeite />} />
              <Route path="hydranten" element={<AussenHydrantenSeite />} />
              <Route path="geraete" element={<AussenGeraeteSeite />} />
              <Route path="versickerung" element={<AussenVersickerungSeite />} />
              <Route path="berechnung" element={<AussenBerechnungSeite />} />
              {/* TS9b2 (E3): der Bereich in der Adresse - Löschwasser ist
                  berechnung/loeschwasser, keine eigene Seite (Gliederung N2). */}
              <Route path="berechnung/:bereich" element={<AussenBerechnungSeite />} />
              <Route path="rueckhaltung" element={<AussenRueckhaltungSeite />} />
              <Route path="nachweis" element={<AussenNachweisSeite />} />
            </Route>
            <Route path="aussparungen" element={<Aussparungen />} />
            {/* Drei eigenstaendige Zugaenge auf Ansichten, die bis N3 NUR ueber
                den internen Reiter von Aussparungen.tsx erreichbar waren
                (Plan Abschnitt 3.6) - direkt verlinkbar, der Reiter bleibt. */}
            <Route path="aussparungen/abgleich" element={<AussparungAbgleichSeite />} />
            <Route path="aussparungen/arbeit" element={<AussparungArbeitSeite />} />
            <Route path="aussparungen/baum" element={<AussparungBaumSeite />} />
            <Route path="ausschreibung"
                   element={<Navigate to="lueftung" replace />} />
            {/* Gesamt-LV ist projektweit (kein Gewerk-Bezug) und steht deshalb
                VOR dem dynamischen Gewerkpfad - eine eigene, statische Route
                mit Sidebar-Eintrag (N3-Nachtrag). */}
            <Route path="ausschreibung/gesamt" element={<AusschreibungGesamtSeite />} />
            <Route path="ausschreibung/:gewerk" element={<Ausschreibung />} />
            {/* Die Grundlagen (Mengen-/Merkmalregeln, Probelauf) haengen am
                Gewerk wie die Positionsliste selbst - direkt verlinkbar, aber
                OHNE Sidebar-Eintrag, aus demselben Grund wie `ausschreibung`
                selbst keinen festen Gewerkpfad traegt (Kopfkommentar
                AusschreibungSeiten.tsx). */}
            <Route path="ausschreibung/:gewerk/grundlagen" element={<AusschreibungGrundlagenSeite />} />

            {/* Der Fremdmodellcheck. Er steht in der OBEREN Zeile neben
                den Gewerkeplanungen und nicht darin: Er ist kein Gewerk,
                sondern eine Sicht auf die Modelle der anderen. */}
            {/* Zwei Reiter: die Wochenaufnahme der Fremdmodelle und die
                Kuechenplanung. Als verschachtelte Routen und nicht als
                Zustandsschalter - ein Kuechengeraet soll verlinkbar sein. */}
            <Route path="fremdmodelle" element={<Fremdmodelle />}>
              <Route index element={<Fremdmodellaufnahme />} />
              <Route path="kueche" element={<Kuechenplanung />} />
              <Route path="kueche/aufnahme" element={<Kuechenaufnahme />} />
              <Route path="kueche/zuordnung" element={<Kuechenmapping />} />
              <Route path="kueche/qualitaet" element={<Kuechenqualitaet />} />
              {/* Zwei weitere Reiter derselben Seite als eigene Route (N3,
                  Plan Abschnitt 3.5): Anschlüsse ohne Gerät und die Pläne.
                  Der Reiter in Kuechenplanung.tsx bleibt zusätzlich bestehen. */}
              <Route path="kueche/anschluesse" element={<KuecheAnschluesseSeite />} />
              <Route path="kueche/plaene" element={<KuechePlaeneSeite />} />
              {/* N5: der erste der zwei Restposten aus N3 — „Räume" trägt
                  eine eigene Abfrage (`kuecheRaeume`) und eine eigene
                  Schreibaktion, „Planpositionen" bleibt bewusst ohne Route
                  (Begründung im Kopfkommentar von `KuecheRaeumeSeite`). */}
              <Route path="kueche/raeume" element={<KuecheRaeumeSeite />} />
            </Route>

            {/* Koordination — hinter den Fremdmodellen, und in der oberen
                Zeile ROT. Sie ist kein Gewerk, sondern das, was zwischen
                den Gewerken geschieht. */}
            <Route path="koordination" element={<Koordination />} />
            <Route path="ai-completion" element={<AiCompletion />} />

            {/* Der Belegungsplan und die Reservierungen — beide unter der
                Koordination und nicht unter einem Gewerk: Sie beantworten,
                was ZWISCHEN den Gewerken frei ist, und jeder Planer sieht
                dieselbe Karte aus seiner eigenen Sicht. */}
            <Route path="koordination/belegung" element={<Belegung />} />
            <Route path="koordination/reservierungen"
                   element={<Reservierungen />} />
            <Route path="koordination/nachtlauf" element={<Nachtlauf />} />
            <Route path="koordination/sammel"
                   element={<Sammelaktionen />} />

            {/* Eine EIGENE Ansicht fuer die Projektleitung und nicht
                dieselbe wie fuer die Planer: Was sie braucht, sind
                Blockaden und Trends, nicht die naechste Aufgabe. */}
            <Route path="koordination/projektleitung"
                   element={<Projektleitung />} />


            {/* N4 (F69): dreizehn der zwanzig internen Karten von
                Koordination.tsx als echte Unterrouten - zusaetzlich zu den
                vier bestehenden Reitern (?reiter=), nicht an deren Stelle.
                Begruendung je Karte in KoordinationSeiten.tsx und
                navigation/struktur.ts. */}
            <Route path="koordination/regeln/einstufungsregeln"
                   element={<KoordinationEinstufungsregelnSeite />} />
            <Route path="koordination/regeln/gewerkevorrang"
                   element={<KoordinationGewerkevorrangSeite />} />
            <Route path="koordination/regeln/gleichrangregeln"
                   element={<KoordinationGleichrangregelnSeite />} />
            <Route path="koordination/regeln/hoehenbaender"
                   element={<KoordinationHoehenbaenderSeite />} />
            <Route path="koordination/regeln/loesungsarten"
                   element={<KoordinationLoesungsartenSeite />} />
            <Route path="koordination/regeln/toleranzen"
                   element={<KoordinationToleranzenSeite />} />
            <Route path="koordination/regeln/ki-anbieter"
                   element={<KoordinationKiAnbieterSeite />} />
            <Route path="koordination/planungsabschnitte"
                   element={<KoordinationPlanungsabschnitteSeite />} />
            <Route path="koordination/pflichtbauteile"
                   element={<KoordinationPflichtbauteileSeite />} />
            <Route path="koordination/kollisionen-und-einstufung"
                   element={<KoordinationKollisionenUndEinstufungSeite />} />
            <Route path="koordination/auswertung"
                   element={<KoordinationAuswertungSeite />} />
            <Route path="koordination/planeraufgaben"
                   element={<KoordinationPlaneraufgabenSeite />} />
            <Route path="koordination/aufnahme-und-stand"
                   element={<KoordinationAufnahmeUndStandSeite />} />
            {/* Sprinkler (Muster N1, Paket N2, Plan Abschnitt 3.2): zehn
                interne Reiter (`useState('bestand')`) wurden zehn echte
                Unterrouten - Rahmen mit `LocalTabs`, `SprinklerRahmen`, wie
                bei Heiz-/Kuehllast, MSR und Aussenanlagen. Der Pfad
                `sprinkler` selbst bleibt unveraendert und zeigt den
                Index-Reiter "Bestand". Die Gliederung steht in
                seiten/sprinkler/abschnitte.ts und wird dort NUR gelesen. */}
            <Route path="sprinkler" element={<SprinklerRahmen />}>
              <Route index element={<SprinklerBestandSeite />} />
              <Route path="raeume" element={<SprinklerRaeumeSeite />} />
              <Route path="netze" element={<SprinklerNetzeSeite />} />
              <Route path="koepfe" element={<SprinklerKoepfeSeite />} />
              <Route path="berechnung" element={<SprinklerBerechnungSeite />} />
              <Route path="nachweis" element={<SprinklerNachweisSeite />} />
              <Route path="steigleitung" element={<SprinklerSteigleitungSeite />} />
              <Route path="geraete" element={<SprinklerGeraeteSeite />} />
              <Route path="technik" element={<SprinklerTechnikSeite />} />
              <Route path="konfiguration" element={<SprinklerKonfigurationSeite />} />
            </Route>

            {/* Die Detailseiten der Geräte. Dieselbe Produktseite wie bei
                Heizung, Sanitär und Elektro — im Rahmen, damit die
                Reiterleiste stehenbleibt und der Rückweg ein Klick ist. */}
            <Route path="sprinkler-geraete/:bauteilId"
                   element={<SprinklerGeraetIntern api={api} />} />
            <Route path="aussen-geraete/:bauteilId"
                   element={<AussenGeraetIntern api={api} />} />

            <Route path="normwerte" element={<Normwerte />} />
            <Route path="einstellungen" element={<Einstellungen />} />
            {/* AC1: ACC verbinden - Verbindung zu Autodesk (AC1a), später Zuordnung und Versionen. */}
            <Route path="acc" element={<AccVerbindenSeite />} />
            <Route path="profile" element={<Profile />} />

            {/* Tuertypen gehoert zum Tuerbuch und hat keinen eigenen Reiter,
                liegt aber im Rahmen: so bleibt die Reiterleiste stehen und der
                Rueckweg ist ein Klick. Als Geschwisterroute waere die Rangfolge
                gegenueber den Reiterpfaden nicht eindeutig. */}
            <Route path="tuertypen" element={<Tuertypen api={api} />} />
          </Route>
          <Route path="/maschinen" element={<Maschinen api={api} />} />
          <Route path="/installation" element={<Installation api={api} />} />
          <Route path="/modellvorgaben" element={<Modellvorgaben />} />
          <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

/**
 * Die Seite zum Parameterfuellgrad.
 *
 * Der Rumpf steht hier und nicht in `seiten/`, weil die Komponente
 * `Parameterfuellgrad` selbst unveraendert bleibt (sie steht weiterhin
 * auch in den Einstellungen zur Verfuegung); gebraucht wird nur die
 * Verbindung zum Rahmen-Kontext.
 *
 * `useOutletContext` reicht GENAU EINE Ebene weit. Diese Route liegt direkt
 * unter `/projekte/:id` (`Projektrahmen`), der den Kontext setzt - deshalb
 * sieht sie ihn. Eine zusaetzliche Layoutroute dazwischen wuerde die Seite
 * weiss machen, solange sie den Kontext nicht ausdruecklich weiterreicht
 * (Vorfall vom 17.09.2026, siehe `rahmen/AppShell.tsx`).
 */
// MP-6: der Ladezustand der Projektliste wohnt jetzt bei der Projektwahl; hier nur weitergereicht (Aufrufer bleiben).
export type { Projektstand };

/**
 * Der Einstieg unter `/projekte` (PT-0a, Projekttrennung F39): mit Projekt der
 * Sprung in seine Übersicht; ohne Projekt nach der Antwort ein Leerzustand mit
 * Erklärung statt eines endlosen Ladehinweises, bei einem Fehler der Fehler.
 */
export function Projekteinstieg({ projekt, stand, pfad }: {
  projekt: Projekt | null;
  stand: Projektstand;
  pfad: string;
}) {
  if (projekt) return <Navigate to={pfad} replace />;
  if (stand === 'fehler') {
    return <ErrorState meldung="Die Projektliste konnte nicht geladen werden. Bitte die Seite neu laden." />;
  }
  if (stand === 'fertig') {
    return (
      <EmptyState
        titel="Ihnen ist kein Projekt freigegeben."
        erklaerung={'Ein Projekt sieht, wer Mitglied ist, oder jeder Angemeldete, wenn es offen ist. '
          + 'Die Freigabe erteilt SystemAdmin oder der Verwalter des Projekts.'}
      />
    );
  }
  return <LoadingState text="Projekt wird geladen …" />;
}

/** Die Seite „Berichte“ im Projektrahmen (TX2). */
export function Berichteseite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  if (!projekt) return <div className="leer">wird geladen …</div>;
  return <Berichte api={api} projektId={projekt.id} />;
}

export function Parameterfuellgradseite() {
  const { api, projekt } = useOutletContext<Rahmen>();
  if (!projekt) return <div className="leer">wird geladen …</div>;
  return <Parameterfuellgrad api={api} projektId={projekt.id} />;
}

function Anmeldung() {
  const { instance } = useMsal();

  return (
    <div className="anmeldung">
      <div className="karte">
        {/* Die Anmeldekarte ist die erste Seite, die jemand sieht - also
            traegt sie die Marke, nicht einen getippten Produktnamen. Zwei
            Zeilen, zwei Aussagen, bewusst getrennt:

              BETREIBER  TEUBER+VIEL GmbH - wer die Plattform betreibt.
              SYSTEM     das MEPLORA-Logo - wie die Plattform heisst.

            Die Firmenzeile steht UEBER dem Logo. Das ist kein Zufall: stuende
            sie darunter, laese sie sich als Unterzeile der Wortmarke lesen,
            und genau das verbietet die CI ("combined with a subtitle",
            `MASTER_LOCK.txt`). Oben stehend ist sie eine eigene Angabe.

            Sie traegt `--text-sekundaer`, nicht mehr den Akzent: seit die
            Karte das rote Logo zeigt, waere eine zweite rote Zeile daneben
            ein zweites Markenzeichen. #565656 auf Weiss = 7,34:1.
            (Bis 19.09.2026 stand hier das Literal #9ab8d0 - 2,07:1 gegen
            Weiss und damit unter jeder WCAG-Schwelle.)

            Das Logo ist die ROTE Fassung: heller Untergrund. Masse wie im
            Kopf gerechnet - 188 px Bildbreite ergeben 180,4 px Zeichenbreite
            (CI-Minimum 180 px), der Schutzraum von 8,7 px steckt in den
            3,7 px Innenrand des SVG plus 5 px Polsterung; die 36 px
            Kartenpolsterung liegen ohnehin weit darueber. */}
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-sekundaer)', letterSpacing: '0.04em' }}>
          TEUBER+VIEL GmbH
        </div>
        <h1 style={{ margin: '14px 0 4px' }}>
          <img
            src="/marke/MEPLORA_Primary_Red.svg"
            alt="MEPLORA"
            style={{ width: 188, height: 'auto', padding: '5px 0', display: 'block', margin: '0 auto' }}
          />
        </h1>
        <p className="sehr-schwach" style={{ marginTop: 0 }}>
          Datenbankgestützt planen · Aufnahme · Berechnung · Rückschreiben
        </p>

        <button
          className="betont"
          style={{ width: '100%', marginTop: 22, padding: 11 }}
          onClick={() => instance.loginRedirect(apiRequest)}
        >
          Mit Firmenkonto anmelden
        </button>
      </div>
    </div>
  );
}
